import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {Stroke} from '../../../../Models/Stroke';
import {formatTime} from '../../../Support/Formatting/formatTime';
import {formatDuration} from '../../../Support/Formatting/formatDuration';
import {calculateRangeOverview} from '../../../../Strokes/calculateRangeOverview';
import {formatDistance} from '../../../Support/Formatting/formatDistance';
import {formatSplit} from '../../../Support/Formatting/formatSplit';
import {RangeInput} from '../../../../Models/RangeInput';
import {PresenterProps, withPresenter} from '../../../../Support/with_presenter';
import {HighlightedRangeSummaryPresenter} from './HighlightedRangeSummaryPresenter';
import {useNavigate} from 'react-router-dom';
import {ObservableNetworkRequestStatusView} from '../../../Components/Utils/ObservableNetworkRequestStatusView';

interface OwnProps {
    workoutId: number;
    workoutUserId: number;
    strokes: Stroke[];
    range: RangeInput;
    onRangeChange: (range: RangeInput | null) => void;
}

const HighlightedRangeSummaryComponent: FunctionComponent<OwnProps & PresenterProps<HighlightedRangeSummaryPresenter>> =
    observer(({workoutId, workoutUserId, strokes, range, onRangeChange, presenter}) => {
        const navigate = useNavigate();


        const overview = calculateRangeOverview(range, strokes);

        return (
            <div style={{position: 'sticky', top: 24}}>
                <div className="card mb-3 mt-lg-0 mt-3">
                    <div className="card-title">
                        <h5>Analyze range</h5>
                    </div>
                    <div className="card-body d-flex flex-column">
                        <p>Dive deeper in a section of your workout</p>
                        <dl className="mb-0">
                            <dt>Range</dt>
                            <dd>
                                {formatTime(range.start).formatted} - {formatTime(range.end).formatted}
                            </dd>
                        </dl>
                        <dl className="mb-0">
                            <dt>Duration</dt>
                            <dd>{formatDuration(overview.totalDuration).formatted}</dd>
                        </dl>
                        <dl className="mb-0">
                            <dt>Distance</dt>
                            <dd>
                                {formatDistance(overview.totalDistance).formatted}
                                {formatDistance(overview.totalDistance).unit}
                            </dd>
                        </dl>
                        <dl className="mb-0">
                            <dt>Split</dt>
                            <dd>
                                {formatSplit(overview.averageSpeed).formatted} {formatSplit(overview.averageSpeed).unit}
                            </dd>
                        </dl>

                        <div className="mb-3">
                            <ObservableNetworkRequestStatusView
                                result={presenter.reports}
                                loader={() => <span>Loading reports</span>}
                                error={() => <span>Error loading reports</span>}
                                component={(data) => (
                                    <>
                                        <label htmlFor="report" className="mb-2 form-label">
                                            Select a report to add this section to
                                        </label>
                                        <select
                                            className="form-select"
                                            value={presenter.selectedReport?.id ?? ''}
                                            name="report"
                                            onChange={(e) => {
                                                presenter.setSelectedReport(
                                                    data.find((report) => {
                                                        // parseInt since mobx proxied objects are not equal
                                                        // @ts-ignore
                                                        // tslint:disable-next-line:triple-equals
                                                        return parseInt(report.id, 10) === parseInt(e.target.value, 10);
                                                    })!,
                                                );
                                            }}
                                        >
                                            <option value="">Select a report</option>
                                            {data.map((report) => (
                                                <option key={report.id} value={report.id}>
                                                    {report.name} - {report.type}
                                                </option>
                                            ))}
                                        </select>
                                    </>
                                )}
                            />
                        </div>

                        <div className="d-flex flex-row justify-content-between">
                            <button className="btn" onClick={() => onRangeChange(null)}>
                                Reset
                            </button>
                            <button
                                className="btn btn-primary"
                                disabled={presenter.disabledForm}
                                onClick={async () => {
                                    const result = await presenter.onCreate(range, overview);
                                    if (result.status === 'success') {
                                        navigate('/reports/' + presenter.selectedReport?.id);
                                    }
                                }}
                            >
                                Save & view
                            </button>
                        </div>
                    </div>
                </div>

                <ul className="list-group mb-3 d-none d-lg-flex">
                    <li className="list-group-item">
                        <a href="#interval-table">Interval table</a>
                    </li>
                    <li className="list-group-item">
                        <a href="#graphs">Graphs</a>
                    </li>
                    <li className="list-group-item">
                        <a href="#split-times">Split times</a>
                    </li>
                    <li className="list-group-item">
                        <a href="#stroke-analyzer">Stroke analyzer</a>
                    </li>
                </ul>
            </div>
        );
    });

export const HighlightedRangeSummary = withPresenter<HighlightedRangeSummaryPresenter, OwnProps>(
    (props, component) =>
        new HighlightedRangeSummaryPresenter(
            props.workoutId,
            component.network.snippets,
            component.network.report,
            component.localPreferences,
        ),
    HighlightedRangeSummaryComponent,
);
